import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { authFetch, authFetchJson } from "@/lib/auth-fetch";
import {
  compactMethodLabel,
  usePaymentMethods,
  type PaymentMethod,
} from "@/lib/payment-methods";
import { useToast } from "./Toast";

/**
 * Operator-facing "Record payment" dialog. Sits on top of the
 * BookingSidePanel and posts to `POST /api/v1/bookings/{id}/payments`.
 *
 * Two flow shapes via the "Mark as verified" checkbox:
 *   - ON  (default): payment lands as VERIFIED and the booking-payment
 *     recomputer flips the booking → CONFIRMED if the deposit threshold
 *     is met. One-click record-and-confirm for cash / pre-confirmed
 *     transfers.
 *   - OFF: payment lands as VERIFYING; surfaces in the verification
 *     queue + the panel's "Approve & confirm" footer button.
 *
 * Optional file upload runs as a follow-up POST to
 * `/api/v1/payments/{id}/receipt` after the payment row is created.
 */

type PaymentDto = {
  id: string;
  status: string;
  amount: number | string;
  currency: string;
};

export function RecordPaymentDialog({
  open,
  onClose,
  bookingId,
  bookingCurrency,
  remainingToConfirm,
  bookingPrice,
}: {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  bookingCurrency: string;
  /** What the booking still needs (in `bookingCurrency`) to reach the
   *  deposit threshold. We default the amount field to this so the
   *  common case is one click + Submit. */
  remainingToConfirm: number;
  bookingPrice: number;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const methods = usePaymentMethods();
  const activeMethods = useMemo(
    () => (methods.data ?? []).filter((m: PaymentMethod) => m.active),
    [methods.data],
  );

  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [markVerified, setMarkVerified] = useState(true);
  const [file, setFile] = useState<File | null>(null);

  // Reset on open + seed amount with what's needed to confirm. Falls back
  // to the booking price when the booking is already past the threshold
  // (e.g. recording an over-payment for some reason).
  useEffect(() => {
    if (!open) return;
    setNotes("");
    setMarkVerified(true);
    setFile(null);
    setAmount(
      String(
        remainingToConfirm > 0 ? remainingToConfirm : bookingPrice,
      ),
    );
  }, [open, remainingToConfirm, bookingPrice]);

  // Auto-pick the first active method when the list loads.
  useEffect(() => {
    if (!open) return;
    if (!paymentMethodId && activeMethods.length > 0) {
      setPaymentMethodId(activeMethods[0].id);
    }
  }, [open, activeMethods, paymentMethodId]);

  const submit = useMutation({
    mutationFn: async () => {
      const numeric = Number(amount);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        throw new Error("Amount must be a positive number.");
      }
      if (!paymentMethodId) throw new Error("Pick a payment method.");
      const created = await authFetchJson<PaymentDto>(
        `/api/v1/bookings/${bookingId}/payments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paymentMethodId,
            amount: numeric,
            notes: notes.trim() || undefined,
            markVerified,
          }),
        },
      );
      // Best-effort receipt upload — doesn't block payment creation.
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await authFetch(`/api/v1/payments/${created.id}/receipt`, {
          method: "POST",
          body: fd,
        });
        if (!res.ok) {
          // Surface as a warning but don't fail the whole record.
          toast.warning(
            "Payment recorded — receipt upload failed",
            `HTTP ${res.status}`,
          );
        }
      }
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["booking", bookingId] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["dashboard.overview"] });
      toast.success(
        markVerified ? "Payment recorded & verified" : "Payment recorded",
        markVerified
          ? "Booking moves toward Confirmed."
          : "Awaiting verification in the queue.",
      );
      onClose();
    },
    onError: (e) => toast.error("Couldn't record payment", (e as Error).message),
  });

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-primary" />
            Record payment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-1.5">
            <Label htmlFor="rp-method">Payment method</Label>
            <Select
              value={paymentMethodId || undefined}
              onValueChange={setPaymentMethodId}
            >
              <SelectTrigger id="rp-method" className="h-10">
                <SelectValue placeholder="Choose a method…" />
              </SelectTrigger>
              <SelectContent>
                {activeMethods.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {compactMethodLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!methods.isLoading && activeMethods.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic">
                No active payment methods.{" "}
                <Link
                  to="/workspace/payment-methods"
                  className="text-primary hover:underline"
                  onClick={onClose}
                >
                  Add one in Workspace → Payment methods →
                </Link>
              </p>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="rp-amount">Amount ({bookingCurrency})</Label>
            <Input
              id="rp-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {remainingToConfirm > 0 && (
              <p className="text-[11px] text-muted-foreground">
                Remaining to confirm: {bookingCurrency}{" "}
                {remainingToConfirm.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </p>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="rp-notes">Reference / notes (optional)</Label>
            <Input
              id="rp-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. GCash ref #ABC123"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="rp-receipt">Receipt file (optional)</Label>
            <Input
              id="rp-receipt"
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>

          <label className="flex items-start gap-3 rounded-md border bg-card px-3 py-2.5 cursor-pointer">
            <Checkbox
              checked={markVerified}
              onCheckedChange={(v) => setMarkVerified(v === true)}
              className="mt-0.5"
              aria-label="Mark as verified"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium">Mark as verified</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Operator-recorded payments are verified by default and move
                the booking toward Confirmed. Uncheck to leave the payment
                in the verification queue instead.
              </p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submit.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => submit.mutate()}
            disabled={submit.isPending || !paymentMethodId || !amount}
          >
            {submit.isPending ? "Recording…" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
